import { gsap } from 'gsap';
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin';
import { CLOSE_TOTAL } from './nav-timing';

// The reduced-motion path replaces the wipe with the root's 300ms cross-fade,
// so the pointer-events block only needs to span that.
const REDUCED_MOTION_CLOSE_MS = 300;

gsap.registerPlugin(MorphSVGPlugin);

// The desktop decoration path is a pentagon tracing the panel outline:
// top-left corner → apex → bottom-left corner → bottom-right → top-right → close.
// The two left corners share an X coordinate; the apex X tracks the active link
// and is clamped between MIN and MAX offsets from the corners so the chevron
// stays a gentle angle instead of collapsing to a sharp V on wide viewports.
// SVG viewBox is 0 0 4112.2 4169.29 with preserveAspectRatio="none", so SVG
// units scale linearly with viewport width/height.
const PANEL_CORNER_X = 1950;
const PANEL_RIGHT_X = 4112.2;
const PANEL_BOTTOM_Y = 4169.29;
const PANEL_DEFAULT_APEX_X = 2150;
const PANEL_DEFAULT_APEX_Y = 2084.64;
const PANEL_APEX_MIN_OFFSET = 100;
const PANEL_APEX_MAX_OFFSET = 300;

const DESKTOP_QUERY = '(min-width: 1024px)';

const isDesktop = () => window.matchMedia(DESKTOP_QUERY).matches;
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const buildDecorationPath = (apexX, apexY) =>
    `M${PANEL_CORNER_X},0 L${apexX.toFixed(2)},${apexY.toFixed(2)} L${PANEL_CORNER_X},${PANEL_BOTTOM_Y} L${PANEL_RIGHT_X},${PANEL_BOTTOM_Y} L${PANEL_RIGHT_X},0 Z`;

const getDecorationPath = () =>
    document.querySelector('#desktop-navigation-decoration path');

// The store is the single source of truth for the navigation overlay, and
// survives page swaps because stores live on Alpine itself, not on any DOM
// node. The overlay element persists via transition:persist, and its local
// x-data scope gets torn down / rebound by Alpine during each swap — reading
// state from $store.nav.* means the rebind finds the same values the persisted
// DOM is already showing, so the swap reads as continuous.
const navStore = {
    open: false,

    // True from the moment a close starts until the mobile wipe has finished
    // covering the screen — see close(). Only the overlay's pointer-events read
    // it; it is deliberately not part of the open/closed visual state.
    closing: false,
    _closingTimer: null,

    // True once the menu has been opened in this session. The header logo uses
    // it to delay its mobile fade-in so it doesn't reappear on top of the
    // overlay while that is still sliding away — a delay that must NOT apply on
    // a fresh load, or the logo would lag the rest of the header's reveal.
    // Deliberately not reset on close, and it survives view transitions along
    // with the rest of the store, which is what the post-navigation auto-close
    // needs.
    hasOpened: false,

    _pendingAutoClose: false,
    _freezeApex: false,

    toggle() {
        if (this.open) this.close();
        else this.openMenu();
    },

    openMenu() {
        this._freezeApex = false;
        this.open = true;
        this.hasOpened = true;
        this._openDecoration();
    },

    // `open` flips immediately, but the wipe keeps the mobile screen covered for
    // CLOSE_TOTAL afterwards. Releasing pointer-events on that first frame left
    // the page beneath tappable while still invisible — a stray tap could
    // navigate somewhere the user couldn't see. `closing` holds the block until
    // the bars are actually gone. Reduced motion skips the wipe, so it only
    // needs the root's cross-fade.
    close() {
        this.open = false;
        this.closing = true;
        clearTimeout(this._closingTimer);
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this._closingTimer = setTimeout(
            () => { this.closing = false; },
            reduced ? REDUCED_MOTION_CLOSE_MS : CLOSE_TOTAL,
        );
    },

    // Continuous apex tracking: call on mousemove, mouseleave, and resize.
    // Frozen during a navigation-driven close so the chevron stays seated
    // at the link the user clicked, instead of snapping back to centre as
    // mouseleave fires when the overlay's pointer-events flip off.
    updateApex() {
        if (this._freezeApex) return;
        const rect = this._resolveApexRect();
        this._syncSvgDecoration(rect);
    },

    // Menu-open animation: swooshes the desktop SVG from a deep apex to the
    // active link before the panel slides in.
    _openDecoration() {
        const rect = this._resolveApexRect({ includeHover: false });
        this._openSvgDecoration(rect);
    },

    // Resolves the link the background-shape apex should point at. A hovered
    // link takes precedence over the current-page link; pass
    // { includeHover: false } to target the active link only (used when
    // opening the menu, before any hover has happened).
    _resolveApexRect({ includeHover = true } = {}) {
        const el = (includeHover && document.querySelector('.svg-target'))
            || document.querySelector('.active-navigation-item');
        return el ? el.getBoundingClientRect() : null;
    },

    _syncSvgDecoration(rect) {
        if (!isDesktop()) return;
        const path = getDecorationPath();
        if (!path) return;

        const point = rect ? this._toSvgPoint(rect) : null;
        const apexX = point ? point.x : PANEL_DEFAULT_APEX_X;
        const apexY = point ? point.y : PANEL_DEFAULT_APEX_Y;

        gsap.to(path, {
            morphSVG: buildDecorationPath(apexX, apexY),
            duration: rect ? 0.5 : 0.4,
            ease: 'power2.out',
            overwrite: true,
        });
    },

    _openSvgDecoration(rect) {
        if (!isDesktop()) return;
        const path = getDecorationPath();
        if (!path) return;

        // Seat the starting "deep" apex at the target Y. Without this, an
        // active link not at viewport mid-height would make the apex drift
        // diagonally during the open tween.
        let targetX = PANEL_DEFAULT_APEX_X;
        let targetY = PANEL_DEFAULT_APEX_Y;
        if (rect) {
            const point = this._toSvgPoint(rect);
            if (point) {
                targetX = point.x;
                targetY = point.y;
            }
        }

        const deepX = targetX + 400;
        path.setAttribute('d', buildDecorationPath(deepX, targetY));
        gsap.to(path, {
            morphSVG: buildDecorationPath(targetX, targetY),
            duration: 0.5,
            ease: 'power2.out',
            overwrite: true,
        });
    },

    // Maps a viewport rect to a point inside the SVG viewBox. The apex seats
    // just left of the link, then is clamped to a bounded offset from the
    // panel corners so the chevron stays a consistent, gentle indent across
    // viewport sizes. Use viewport dimensions directly — the decoration is
    // fixed inset-0 at rest, but getBoundingClientRect() would reflect any
    // in-flight translate, skewing the math during the slide transition.
    _toSvgPoint(rect) {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        if (!vw || !vh) return null;

        const centerY = rect.top + rect.height / 2;
        const rawX = (rect.left - 100) * (PANEL_RIGHT_X / vw);
        return {
            x: clamp(
                rawX,
                PANEL_CORNER_X + PANEL_APEX_MIN_OFFSET,
                PANEL_CORNER_X + PANEL_APEX_MAX_OFFSET,
            ),
            y: centerY * (PANEL_BOTTOM_Y / vh),
        };
    },
};

// Registers the nav store and the handful of document-level listeners it
// needs. Called once from the Alpine entrypoint; the listeners are attached
// to `document` / `window` and persist across Astro view transitions, so we
// intentionally don't re-register on astro:page-load.
export const registerNavStore = (Alpine) => {
    Alpine.store('nav', navStore);

    // Apex tracking — fires while the menu is open and the pointer moves over
    // a link (`.svg-target`) or while an active link is focused.
    document.addEventListener('mousemove', () => {
        if (document.querySelector('.svg-target') || document.querySelector('.active-navigation-item')) {
            Alpine.store('nav').updateApex();
        }
    });

    let resizeTimeout;
    window.addEventListener('resize', () => {
        if (!Alpine.store('nav').open) return;
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => Alpine.store('nav').updateApex(), 150);
    });

    // Auto-close orchestration: if a swap begins while the overlay is open,
    // leave it open as a "curtain", then close it shortly after the new page
    // has loaded so its intro animations land behind the fade-out.
    document.addEventListener('astro:before-preparation', () => {
        const nav = Alpine.store('nav');
        if (!nav.open) return;
        nav._pendingAutoClose = true;
        nav._freezeApex = true;
    });
    document.addEventListener('astro:page-load', () => {
        const nav = Alpine.store('nav');
        if (!nav._pendingAutoClose) return;
        nav._pendingAutoClose = false;
        setTimeout(() => nav.close(), 750);
    });
};
